import { useEffect, useMemo, useRef, useState } from "react";
import {
  Ban,
  Bell,
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  CircleUserRound,
  CreditCard,
  Layers3,
  LogOut,
  Menu,
  Moon,
  PhoneCall,
  Search,
  Settings2,
  Sun,
  Users,
  XCircle,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth, useHasPermission } from "../../context/AuthContext";
import { canManageAgents } from "../../lib/userAccess";
import { useCreditAlert } from "../../context/CreditAlertContext";
import { useActiveBatches } from "../../features/batches/queries";
import type { Batch } from "../../features/batches/types";
import { useAskStore } from "../../features/home/ask-store";
import { usePersistentState } from "../../hooks/use-persistent-state";
import { cn } from "../../lib/utils";
import { SUPERVISION_SECTION_STORAGE_KEY, type SupervisionSection } from "../../features/supervision/supervision-section";
import { useNavStore } from "./nav-store";
import { ScoutButton } from "../ui/scout-button";
import { CreditBalanceControl } from "./CreditBalanceControl";
import { AddCreditButton } from "./AddCreditButton";
import { SfaNotificationBell } from "./SfaNotificationBell";
import { OrgSwitcher } from "./OrgSwitcher";

interface CreditNotification {
  id: string;
  kind: "credit";
  title: string;
  detail: string;
  createdAt: string;
  href: string;
  severity: "warning" | "critical";
}

interface BatchNotification {
  id: string;
  kind: "batch";
  title: string;
  detail: string;
  createdAt: string;
  href: string;
  status: "completed" | "failed" | "cancelled";
}

type NotificationItem = CreditNotification | BatchNotification;

export function getUserInitials(name?: string, email?: string) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length > 0) return parts.map((part) => part[0]).join("").slice(0, 3).toUpperCase();
  return String(email || "U").trim().charAt(0).toUpperCase() || "U";
}

function formatWhen(value: string) {
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return "";
  const elapsed = Math.max(0, Date.now() - timestamp);
  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days < 7 ? `${days}d ago` : new Date(value).toLocaleDateString("en-IN");
}

function batchNotification(batch: Batch): BatchNotification | null {
  if (!batch.finished_at || !["completed", "failed", "cancelled"].includes(batch.status)) return null;
  const status = batch.status as BatchNotification["status"];
  const completed = Number(batch.completed || 0);
  const total = Number(batch.total || 0);
  const title = status === "completed"
    ? "Batch completed"
    : status === "failed"
      ? "Batch needs attention"
      : "Batch cancelled";

  return {
    id: `batch:${batch.schedule_id}:${status}:${batch.finished_at}`,
    kind: "batch",
    title,
    detail: `${batch.name} - ${completed.toLocaleString("en-IN")}/${total.toLocaleString("en-IN")} calls completed`,
    createdAt: batch.finished_at,
    href: "/batch",
    status,
  };
}

export function TopBar({ theme = "light", onThemeChange }: { theme?: "light" | "dark"; onThemeChange?: () => void }) {
  const { logout, user } = useAuth();
  const canManageSupervisionRules = useHasPermission("supervision.rules.manage");
  const { creditAlert } = useCreditAlert();
  const { pathname, search: routeSearch } = useLocation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLFormElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [globalQuery, setGlobalQuery] = useState("");
  const [supervisionSection, setSupervisionSection] = usePersistentState<SupervisionSection>(
    SUPERVISION_SECTION_STORAGE_KEY,
    "live",
  );
  const openAsk = useAskStore((state) => state.openAsk);
  const { mobileOpen, openMobileNav } = useNavStore();
  const isAdminRoute = pathname.startsWith("/admin/");
  const isSfaRoute = pathname.startsWith("/crm");
  const storageScope = String(user?.id || user?.email || "user");
  const batches = useActiveBatches(!isAdminRoute, storageScope);
  const [creditNotifications, setCreditNotifications] = usePersistentState<CreditNotification[]>(
    `notifications:credits:${storageScope}`,
    [],
  );
  const [readIds, setReadIds] = usePersistentState<string[]>(
    `notifications:read:${storageScope}`,
    [],
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!menuOpen && !notificationsOpen && !searchOpen) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target)) setMenuOpen(false);
      if (!notificationsRef.current?.contains(target)) setNotificationsOpen(false);
      if (!searchRef.current?.contains(target)) setSearchOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen, notificationsOpen, searchOpen]);

  useEffect(() => {
    setGlobalQuery(new URLSearchParams(routeSearch).get("q") || "");
  }, [pathname, routeSearch]);

  useEffect(() => {
    if (!creditAlert || typeof creditAlert !== "object") return;
    const remaining = Number(creditAlert.remaining);
    const severity = creditAlert.severity === "critical" || remaining <= 0 ? "critical" : "warning";
    const id = String(creditAlert.id || `credit:${creditAlert.timestamp || Date.now()}`);
    const next: CreditNotification = {
      id,
      kind: "credit",
      title: severity === "critical" ? "Credits exhausted" : "Credit balance is low",
      detail: severity === "critical"
        ? "Calls are paused until credits are added."
        : `Balance remaining: Rs ${Math.max(remaining || 0, 0).toLocaleString("en-IN")}`,
      createdAt: String(creditAlert.timestamp || new Date().toISOString()),
      href: "/billing",
      severity,
    };
    setCreditNotifications((current) => (
      current.some((item) => item.id === id) ? current : [next, ...current].slice(0, 20)
    ));
  }, [creditAlert, setCreditNotifications]);

  const notifications = useMemo<NotificationItem[]>(() => {
    const batchItems = (isAdminRoute ? [] : batches.data ?? [])
      .map(batchNotification)
      .filter((item): item is BatchNotification => item !== null);
    return [...creditNotifications, ...batchItems]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 30);
  }, [batches.data, creditNotifications, isAdminRoute]);

  const readSet = useMemo(() => new Set(readIds), [readIds]);
  const unreadCount = notifications.filter((item) => !readSet.has(item.id)).length;
  const initials = getUserInitials(user?.name, user?.email);

  const openNotification = (item: NotificationItem) => {
    setReadIds((current) => current.includes(item.id) ? current : [...current, item.id]);
    setNotificationsOpen(false);
    navigate(item.href);
  };

  const searchTargets = isAdminRoute
    ? [{ label: "Search user access", href: "/admin/users", icon: Users }]
    : [
        { label: "Search calls", href: "/call-history", icon: PhoneCall },
        { label: "Search batches", href: "/batch", icon: Layers3 },
        { label: "Search agents", href: "/", icon: Users },
      ];
  const runSearch = (href: string) => {
    const query = globalQuery.trim();
    if (!query) {
      const params = new URLSearchParams(routeSearch);
      if (params.has("q")) {
        params.delete("q");
        const paramStr = params.toString();
        navigate(paramStr ? `${pathname}?${paramStr}` : pathname);
      }
      setSearchOpen(false);
      return;
    }
    const params = new URLSearchParams({ q: query });
    navigate(`${href}?${params.toString()}`);
    setSearchOpen(false);
  };
  const defaultSearchTarget = isAdminRoute
    ? searchTargets[0]
    : pathname.startsWith("/batch")
      ? searchTargets[1]
      : pathname === "/" || pathname === "/home"
        ? searchTargets[2]
        : searchTargets[0];

  return (
    <header className="relative z-30 flex h-16 shrink-0 items-center gap-2 sm:gap-4 border-b border-line bg-elevated/80 px-3 sm:px-4 md:px-6 backdrop-blur-md">
      {/* Mobile Hamburger Menu Toggle with >=44px touch area */}
      <button
        type="button"
        onClick={openMobileNav}
        aria-expanded={mobileOpen}
        aria-controls="mobile-nav"
        aria-label="Open navigation menu"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-line bg-soft text-ink-2 transition-colors hover:bg-line hover:text-ink md:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>

      <form
        ref={searchRef}
        className="relative flex-1 max-w-[170px] sm:max-w-xs md:max-w-sm lg:w-96"
        onSubmit={(event) => {
          event.preventDefault();
          runSearch(defaultSearchTarget.href);
        }}
      >
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
        <input
          ref={inputRef}
          type="text"
          value={globalQuery}
          onChange={(event) => {
            const val = event.target.value;
            setGlobalQuery(val);
            if (!val.trim()) {
              const params = new URLSearchParams(routeSearch);
              if (params.has("q")) {
                params.delete("q");
                const paramStr = params.toString();
                navigate(paramStr ? `${pathname}?${paramStr}` : pathname);
              }
              setSearchOpen(false);
            } else {
              setSearchOpen(true);
            }
          }}
          onFocus={() => setSearchOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setSearchOpen(false);
              inputRef.current?.blur();
            }
          }}
          placeholder="Search calls, batches, agents..."
          className="w-full rounded-lg border border-transparent bg-soft py-2 pl-9 pr-3 sm:pr-12 text-sm text-ink placeholder:text-ink-faint focus:border-accent-500 focus:bg-elevated focus:outline-none focus:ring-2 focus:ring-accent-500/20"
        />
        <kbd className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 rounded border border-line bg-elevated px-1.5 py-0.5 font-mono text-[10px] text-ink-faint hidden sm:inline-block">
          Ctrl K
        </kbd>
        {searchOpen && globalQuery.trim() && (
          <div className="absolute left-0 right-0 top-11 overflow-hidden rounded-xl border border-line bg-elevated py-1.5 shadow-lg">
            {searchTargets.map((target) => {
              const Icon = target.icon;
              return (
                <button
                  key={target.href}
                  type="button"
                  onClick={() => runSearch(target.href)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-ink-muted transition-colors hover:bg-soft hover:text-ink"
                >
                  <Icon className="h-4 w-4 text-ink-faint" />
                  <span>{target.label}</span>
                  <span className="ml-auto max-w-44 truncate font-mono text-xs text-ink-faint">{globalQuery.trim()}</span>
                </button>
              );
            })}
          </div>
        )}
      </form>
      {onThemeChange && (
        <button
          type="button"
          onClick={onThemeChange}
          aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
          title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
          className="ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-line bg-soft text-ink-muted transition-colors hover:border-accent-500 hover:text-accent-700 dark:hover:text-accent-400"
        >
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
      )}

      {pathname.startsWith("/supervision") ? (
        <div className="ml-1 flex items-center gap-1">
          <nav aria-label="Supervision sections" className="hidden rounded-lg border border-line bg-soft p-0.5 sm:flex">
            {([
              { id: "live", label: "Live supervision", path: "/supervision" },
              { id: "flagged", label: "Flagged", path: "/supervision/flagged" },
              { id: "insights", label: "Insights", path: "/supervision/insights" },
            ] as const).map(({ id: section, label, path }) => {
              const active =
                pathname === path ||
                (section === "live" && (pathname === "/supervision" || pathname === "/supervision/"));
              return (
                <button
                  key={section}
                  type="button"
                  aria-pressed={active}
                  onClick={() => {
                    setSupervisionSection(section);
                    navigate(path);
                  }}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-[11px] font-medium transition",
                    active
                      ? "bg-elevated text-ink shadow-sm"
                      : "text-ink-faint hover:text-ink",
                  )}
                >
                  {label}
                </button>
              );
            })}
          </nav>
          {canManageSupervisionRules ? (
            <button
              type="button"
              onClick={() => navigate("/supervision/rules")}
              aria-label="Open supervision rule settings"
              title="Alert rules"
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-lg border border-line transition",
                pathname === "/supervision/rules"
                  ? "bg-accent-50 text-accent-700 dark:bg-accent-500/10 dark:text-accent-400"
                  : "bg-soft text-ink-faint hover:text-ink",
              )}
            >
              <Settings2 size={14} />
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="flex-1" />

      <OrgSwitcher />

      {(user?.command_center !== false || canManageAgents(user)) && (
        <ScoutButton
          onClick={() => openAsk()}
          aria-label="Open Scout"
          title="Scout"
        />
      )}

      <CreditBalanceControl />
      <AddCreditButton />

      {isSfaRoute ? <SfaNotificationBell /> : <div className="relative" ref={notificationsRef}>
        <button
          aria-label="Notifications"
          aria-expanded={notificationsOpen}
          aria-haspopup="menu"
          onClick={() => {
            setNotificationsOpen((open) => !open);
            setMenuOpen(false);
          }}
          className="relative rounded-lg p-2 text-ink-muted transition-colors hover:bg-soft hover:text-ink"
        >
          <Bell className="h-[18px] w-[18px]" />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-elevated bg-accent-500 px-1 text-[10px] font-bold text-ink">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>

        {notificationsOpen && (
          <div className="absolute right-0 top-12 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-line bg-elevated shadow-lg">
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <div>
                <p className="text-sm font-bold text-ink">Notifications</p>
                <p className="text-xs text-ink-faint">{unreadCount} unread</p>
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={() => setReadIds((current) => Array.from(new Set([...current, ...notifications.map((item) => item.id)])))}
                  className="flex items-center gap-1.5 text-xs font-semibold text-accent-700 dark:text-accent-400 hover:underline"
                >
                  <CheckCheck className="h-3.5 w-3.5" /> Mark all read
                </button>
              )}
            </div>
            <div className="max-h-[26rem] overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="px-6 py-10 text-center">
                  <Bell className="mx-auto h-6 w-6 text-ink-3" />
                  <p className="mt-2 text-sm font-semibold text-ink">No notifications yet</p>
                  <p className="mt-1 text-xs text-ink-3">Batch and credit updates will appear here.</p>
                </div>
              ) : notifications.map((item) => {
                const unread = !readSet.has(item.id);
                const Icon = item.kind === "credit"
                  ? CreditCard
                  : item.status === "completed"
                    ? CheckCircle2
                    : item.status === "failed"
                      ? XCircle
                      : Ban;
                const iconTone = item.kind === "credit"
                  ? item.severity === "critical" ? "text-danger bg-danger-soft" : "text-warning bg-warning-soft"
                  : item.status === "completed" ? "text-success bg-success-soft" : item.status === "failed" ? "text-danger bg-danger-soft" : "text-ink-3 bg-soft";
                return (
                  <button
                    key={item.id}
                    role="menuitem"
                    onClick={() => openNotification(item)}
                    className={cn(
                      "flex w-full gap-3 border-b border-line px-4 py-3 text-left transition-colors last:border-0 hover:bg-soft",
                      unread && "bg-accent-50/60 dark:bg-accent-500/10",
                    )}
                  >
                    <span className={cn("mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", iconTone)}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-start justify-between gap-3">
                        <span className="text-sm font-semibold text-ink">{item.title}</span>
                        {unread && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent-500" />}
                      </span>
                      <span className="mt-0.5 block text-xs leading-5 text-ink-muted">{item.detail}</span>
                      <span className="mt-1 block text-[10px] font-medium uppercase text-ink-faint">{formatWhen(item.createdAt)}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>}

      <div className="relative" ref={menuRef}>
        <button
          onClick={() => {
            setMenuOpen((open) => !open);
            setNotificationsOpen(false);
          }}
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          className="flex items-center gap-1.5 rounded-lg p-1 transition-colors hover:bg-soft"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-line px-1 text-[10px] font-bold text-ink">
            {initials}
          </span>
          <ChevronDown className={cn("h-4 w-4 text-ink-3 transition-transform", menuOpen && "rotate-180")} />
        </button>

        {menuOpen && (
          <div role="menu" className="absolute right-0 top-12 w-56 rounded-xl border border-line bg-elevated py-2 shadow-lg">
            <div className="border-b border-line px-4 pb-2">
              <p className="truncate text-sm font-semibold text-ink">{user?.name || "User"}</p>
              <p className="truncate text-xs text-ink-3">{user?.email || ""}</p>
            </div>
            <button
              onClick={() => {
                setMenuOpen(false);
                navigate("/settings");
              }}
              role="menuitem"
              className="mt-1 flex w-full items-center gap-3 px-4 py-2 text-sm text-ink-2 transition-colors hover:bg-soft hover:text-ink"
            >
              <CircleUserRound className="h-4 w-4" /> Profile
            </button>
            <div className="my-1 border-t border-line" />
            <button
              onClick={logout}
              role="menuitem"
              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-danger transition-colors hover:bg-danger-soft"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        )}
      </div>
    </header>
  );
}
